import { Component, OnInit } from '@angular/core';
import {
  FormGroup,
  FormArray,
  FormBuilder,
  Validators,
  FormControl,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { Subject, debounceTime } from 'rxjs';
import { AccountManagementService } from 'src/app/services/account-management.service';
import { BranchManagementService } from 'src/app/services/branch-management.service';
import { GenericHttpService } from 'src/app/services/generic-http.service';
import { StateService } from 'src/app/services/state.service';
import { UserDashboardService } from 'src/app/services/user-dashboard.service';
import { USER_FORM_CONSTANTS } from 'src/app/models/user-form.constants';
import { StorageService } from 'src/app/services/storage.service';

@Component({
    selector: 'app-branch-form',
    templateUrl: './branch-form.component.html',
    styleUrls: ['./branch-form.component.scss'],
    standalone: false
})
export class BranchFormComponent implements OnInit {
  readonly constants = USER_FORM_CONSTANTS;
  branchId!: number;
  editMode: boolean = false;
  editbranchForm: FormGroup;
  addbranchForm: FormGroup;
  addnewbranchDetails!: FormArray;
  submitted: boolean = false;
  accountDetails: any = [];
  states: any = [];
  organisation: any = {};
  branchType: any = [];
  organisationName: any;
  accountNameDetails: any;
  public phonePattern: string = '^[6-9]\\d{9}$';
  previousEmail: string;
  userRole: string;
  previousContact: string;
  accountId: any;
  onSearchTextChange: Subject<string> = new Subject<string>();
  filteredAccountDetails: any[][] = [];

  accountFilter = {
    search: '',
  };
  constructor(
    private activatedRouter: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private accountManagementService: AccountManagementService,
    private stateService: StateService,
    private branchManagementService: BranchManagementService,
    private genericHttpService: GenericHttpService,
    private userDashboardService: UserDashboardService,
    private storageService: StorageService
  ) {
    this.activatedRouter.params.subscribe((params) => {
      this.branchId = params['id'];
      if (this.branchId) {
        this.editMode = true;
        this.editbranchDetails();
      }
    });

    this.onSearchTextChange.pipe(debounceTime(1500)).subscribe((value) => {
      this.accountFilter.search = value;
      this.accountFilter = Object.assign({}, this.accountFilter);
      value === ''
        ? this.getAccountList()
        : this.getFilteredAccountDqetail(this.accountFilter);
    });

    this.editbranchForm = this.formBuilder.group({
      name: ['', [Validators.required, this.nameValidator]],
      type: ['', [Validators.required]],
      organizationName: [''],
      email: ['', [Validators.required, Validators.email]],
      contact: [
        '',
        [
          Validators.required,
          Validators.pattern(this.phonePattern),
          Validators.maxLength(10),
          Validators.minLength(10),
        ],
      ],
      pinCode: ['', [Validators.required, Validators.maxLength(6)]],
      address: ['', [Validators.required]],
      accountName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      status: [''],
    });

    this.addbranchForm = this.formBuilder.group({
      addnewbranchDetails: this.formBuilder.array([]),
    });
  }

  nameValidator(control: FormControl) {
    const value = control.value;

    // Check if empty or only spaces
    if (!value || value.trim().length === 0) {
      return { noOnlySpaces: true };
    }

    // Check for special characters (allow only letters, numbers, and spaces)
    const validPattern = /^[a-zA-Z0-9\s]+$/;
    if (!validPattern.test(value)) {
      return { invalidCharacters: true };
    }

    return null; // Validation passes
  }

  noOnlySpacesValidator(control: FormControl) {
    if (control.value && control.value.trim().length === 0) {
      return { noOnlySpaces: true };
    }
    return null; // Validation passes
  }
  ngOnInit() {
    this.userRole = this.storageService.getRole() || '';
    this.addNewbranchDetails();
    this.getAccountList();
    this.getStates();
    this.getFilters();
    this.getAccountList();
  }

  filterAccounts(searchTerm: string, index: number): void {
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredAccountDetails[index] = [...this.accountDetails];
    } else {
      const searchLower = searchTerm.toLowerCase().trim();
      this.filteredAccountDetails[index] = this.accountDetails.filter(
        (account) =>
          account.account_name &&
          account.account_name.toLowerCase().includes(searchLower)
      );
    }
  }

  getStates() {
    this.stateService.getState().subscribe((res: any) => {
      this.states = res['content'];
    });
  }

  getFilters() {
    this.genericHttpService
      .getFilters({ filters: 'branch_type' })
      .subscribe((res: any) => {
        this.branchType = res['content']['branch_type'];
      });
  }

  getAccountList(): void {
    this.accountManagementService
      .getAccountDetails('')
      .subscribe((res: any) => {
        this.accountDetails = res['content'];
        this.accountDetails = this.accountDetails.filter(
          (element) => element.active_status === true
        );
        // Update filteredAccountDetails for all existing form groups
        this.filteredAccountDetails = this.branchDetailsArray().controls.map(
          () => [...this.accountDetails]
        );
        if (this.accountDetails.length > 0) {
          this.organisationName = this.accountDetails[0].organisation_name;
          // Update organizationName in all form groups
          this.branchDetailsArray().controls.forEach((control) => {
            control.get('organizationName')?.setValue(this.organisationName);
          });
        }
      });
  }

  getFilteredAccountDqetail(accountFilter) {
    this.userDashboardService
      .getFilteredAccountDqetail(accountFilter)
      .subscribe((res: any) => {
        this.accountDetails = res.content;
      });
  }

  onAccountFilter(evt: any, index: number): void {
    const searchValue = evt.filter || '';
    this.filterAccounts(searchValue, index);
  }

  removebranch(index: number): void {
    this.branchDetailsArray().removeAt(index);
    this.filteredAccountDetails.splice(index, 1); // Remove filtered accounts for this index
    this.addbranchForm.markAsUntouched();
    this.addbranchForm.markAsPristine();
    this.submitted = false;
    this.branchDetailsArray().updateValueAndValidity();
  }

  branchDetailsArray(): FormArray {
    return this.addbranchForm.get('addnewbranchDetails') as FormArray;
  }

  addNewbranchDetails(): void {
    if (this.addbranchForm.controls['addnewbranchDetails'].valid) {
      this.branchDetailsArray().push(this.initbranchDetails());
      const newIndex = this.branchDetailsArray().length - 1;
      const newUserFormGroup = this.branchDetailsArray().at(
        newIndex
      ) as FormGroup;
      this.resetFormValidationState(newUserFormGroup);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'New branch',
        detail: 'Please fill branch details before adding New',
      });
    }
  }
  private resetFormValidationState(formGroup: FormGroup) {
    formGroup.markAsUntouched();
    formGroup.markAsPristine();

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
      }
    });
  }
  initbranchDetails(): FormGroup {
    const formGroup = this.formBuilder.group({
      name: new FormControl('', [Validators.required, this.nameValidator]),
      type: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      contact: new FormControl('', [
        Validators.required,
        Validators.pattern(this.phonePattern),
        Validators.minLength(10),
        Validators.maxLength(10),
      ]),
      organizationName: new FormControl(this.organisationName, [
        Validators.required,
      ]),
      pinCode: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]),
      address: new FormControl('', [Validators.required]),
      accountName: new FormControl('', [Validators.required]),
      stateName: new FormControl('', [Validators.required]),
      status: new FormControl('Active'),
    });
    // Initialize filtered accounts with current accountDetails
    this.filteredAccountDetails.push([...this.accountDetails]);
    return formGroup;
  }

  numberOnly(event: any) {
    const pattern = /[0-9\-]/;
    let inputChar = String.fromCharCode(event.charCode);
    if (event.keyCode != 8 && !pattern.test(inputChar)) {
      event.preventDefault();
    }
  }

  gotoDashboard() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  gotoListing() {
    this.router.navigate(['/doqfy/management/branch-listing']);
  }

  editbranchDetails() {
    this.branchManagementService
      .getBranchDetailById(this.branchId)
      .subscribe((res: any) => {
        var branch = res['content'][0];
        this.accountId = branch.account;

        // Check if contact number is masked
        const isMaskedContact =
          branch.contact_number && branch.contact_number.includes('*');

        this.editbranchForm.patchValue({
          name: branch.name,
          type: branch.branch_type,
          organizationName: branch.organisation_name,
          email: branch.email,
          contact: branch.contact_number,
          pinCode: branch.pincode,
          address: branch.address,
          accountName: branch.account_name,
          stateName: branch.state.id,
          status: branch.is_active ? 'Active' : 'Inactive',
        });

        // If contact is masked, clear validators temporarily
        if (isMaskedContact) {
          this.editbranchForm.get('contact')?.clearValidators();
          this.editbranchForm
            .get('contact')
            ?.setValidators([Validators.required]);
          this.editbranchForm.get('contact')?.updateValueAndValidity();
        }

        this.accountNameDetails = branch.account_name;
        this.previousEmail = branch.email;
        this.previousContact = branch.contact_number;
      });
  }

  savebranch() {
    this.submitted = true;

    // Mark all controls as touched to show validation errors
    this.addbranchForm.markAllAsTouched();

    if (this.addbranchForm.invalid) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fix all validation errors before submitting',
      });
      return;
    }

    const addnewbranchDetailsFormArray = this.addbranchForm.get(
      'addnewbranchDetails'
    ) as FormArray;

    // Additional check: ensure at least one branch is added
    if (addnewbranchDetailsFormArray.length === 0) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please add at least one branch',
      });
      return;
    }

    const branchDetailsArray: {
      name: string;
      account_id: number;
      address: string;
      branch_type: string;
      email: string;
      contact_number: string;
      state_id: number;
      pincode: string;
    }[] = [];

    // Iterate over each FormGroup in the FormArray
    addnewbranchDetailsFormArray.controls.forEach(
      (abstractControl: AbstractControl) => {
        if (abstractControl instanceof FormGroup) {
          const formGroup = abstractControl as FormGroup;
          var branchData = {
            name: formGroup.controls['name'].value.trim(), // Trim spaces
            account_id: Number(formGroup.controls['accountName'].value),
            address: formGroup.controls['address'].value.trim(),
            branch_type: formGroup.controls['type'].value,
            email: formGroup.controls['email'].value.trim(),
            contact_number: formGroup.controls['contact'].value,
            state_id: Number(formGroup.controls['stateName'].value),
            pincode: formGroup.controls['pinCode'].value,
          };
          branchDetailsArray.push(branchData);
        }
      }
    );

    this.branchManagementService.createBranch(branchDetailsArray).subscribe(
      (res: any) => {
        if (res.status == 201) {
          this.messageService.add({
            severity: 'success',
            detail: 'Branch added successfully',
          });
          setTimeout(() => {
            this.router.navigate(['/doqfy/management/branch-listing']);
          }, 500);
        }
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          detail: 'Failed to add branch. Please try again.',
        });
      }
    );
  }

  editbranch() {
    this.submitted = true;

    // Mark all controls as touched to show validation errors
    this.editbranchForm.markAllAsTouched();

    if (this.editbranchForm.invalid) {
      this.messageService.add({
        severity: 'error',
        detail: 'Please fix all validation errors before submitting',
      });
      return;
    }

    var branchData = {
      branch_id: this.branchId,
      name: this.editbranchForm.controls['name'].value.trim(),
      account_id: this.accountId,
      address: this.editbranchForm.controls['address'].value.trim(),
      branch_type: this.editbranchForm.controls['type'].value,
      email: this.editbranchForm.controls['email'].value.trim(),
      contact_number: this.editbranchForm.controls['contact'].value,
      state_id: Number(this.editbranchForm.controls['stateName'].value),
      pincode: this.editbranchForm.controls['pinCode'].value,
    };

    if (this.previousEmail === branchData.email) {
      delete branchData.email;
    }
    // If contact hasn't changed or is masked, don't send it
    if (
      this.previousContact === branchData.contact_number ||
      (this.previousContact &&
        this.previousContact.includes('*') &&
        branchData.contact_number === this.previousContact)
    ) {
      delete branchData.contact_number;
    }

    this.branchManagementService
      .editBranch(Number(this.branchId), branchData)
      .subscribe(
        (res: any) => {
          if (res.status == 200) {
            this.messageService.add({
              severity: 'success',
              detail: 'Branch edited successfully',
            });
            setTimeout(() => {
              this.router.navigate(['/doqfy/management/branch-listing']);
            }, 500);
          }
        },
        (error) => {
          this.messageService.add({
            severity: 'error',
            detail: 'Failed to edit branch. Please try again.',
          });
        }
      );
  }

  // numberOnlyForContact(event: any) {
  //   const pattern = /[0-9\-]/;
  //   let inputChar = String.fromCharCode(event.charCode);
  //   if (event.keyCode != 8 && !pattern.test(inputChar) || event.keyCode == 45) {
  //     event.preventDefault();
  //   } else if (event.target.value === '0') {
  //     event.preventDefault();
  //   }
  // }

  numberOnlyForContact(event: KeyboardEvent) {
    const inputChar = String.fromCharCode(event.charCode);
    const currentValue = (event.target as HTMLInputElement).value;

    if (
      [
        'Backspace',
        'Delete',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Control',
      ].includes(event.key)
    ) {
      return;
    }

    if (event.ctrlKey && event.key === 'v') {
      return;
    }

    if (!/^\d$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    if (currentValue.length === 0 && !/[6-9]/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    if (currentValue.length >= 10) {
      event.preventDefault();
    }
  }

  handlePasteEvent(event: ClipboardEvent) {
    const paste = event.clipboardData.getData('text');
    const regex = new RegExp(this.constants.ALPHANUMERIC_SPACE_PATTERN);
    if (!regex.test(paste)) {
      event.preventDefault();
    }
  }
}
